import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';

import sendRequest from '../../../service/sendRequest';
import { enqueueSnackbar } from '../../notifier/notifierActions';
import { changeConfirmed, changeSonarConfirmed } from '../consumerReducer';
import { SONAR_PROJECT_RANGE_TYPE, SONAR_RANGE_TYPE } from '../../../constants';
import errorNotification from '../../../utils/errorNotification';
import { constructSonarRequestDates } from '../../sonar/sonarActions';
import { cancelNavig8, setNavig8AbortController } from '../../sonar/sonarReducer';

const GET_CONSUMER_SONAR_DATA = 'consumer/sonar/GET_CONSUMER_SONAR_DATA';
const CONSUMER_SONAR_LOGIN = 'consumer/sonar/CONSUMER_SONAR_LOGIN';
const GET_CONSUMER_SONAR_NAVIG8 = 'consumer/sonar/GET_CONSUMER_SONAR_NAVIG8';
const REPLY_CONSUMER_SONAR_NAVIG8 = 'consumer/sonar/REPLY_CONSUMER_SONAR_NAVIG8';

export const consumerSonarLogin = createAsyncThunk(
  CONSUMER_SONAR_LOGIN,
  async ({ projectName, projectPassword }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () => axios.post('/api/consumer/sonar/login', { projectName, projectPassword }),
      {
        onError: (e) => {
          errorNotification(e, [
            { code: 403, errorText: 'wrongCredentials', errorSubText: 'pleaseTryAgain' },
            { code: 404, errorText: 'noDataCollectedYet', errorSubText: 'accessToTheProjectIsCurrentlyDenied' },
          ]);
          dispatch(changeConfirmed({ confirmed: false }));

          return rejectWithValue(e);
        },
      },
    );

    const { collectedFrom, collectedTill, rangeType } = response.data;

    if (!collectedFrom || !collectedTill) {
      dispatch(
        enqueueSnackbar({
          message: 'noDataCollectedYet',
          options: {
            variant: 'error',
            subMessage: 'accessToTheProjectIsCurrentlyDenied',
          },
        }),
      );

      return rejectWithValue(null);
    }

    dispatch(changeSonarConfirmed({ confirmed: true, projectName, projectPassword }));
    return { collectedFrom, collectedTill, rangeType: rangeType || SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE };
  },
);

export const getConsumerSonarData = createAsyncThunk(
  GET_CONSUMER_SONAR_DATA,
  async (_, { getState, rejectWithValue }) => {
    const {
      [SONAR_RANGE_TYPE.COMPARABLE]: { startDate: comparableStartDate, endDate: comparableEndDate },
      [SONAR_RANGE_TYPE.COMPARE_TO]: { startDate: compareToStartDate, endDate: compareToEndDate },
    } = getState().sonarDashboard.dateRanges;
    const serverUrl = getState().securityNew.serverUrl;
    const { projectName, projectPassword } = getState().consumer.sonar;

    if (!projectName || !projectPassword) {
      return rejectWithValue(null);
    }

    const ranges = constructSonarRequestDates({
      comparableStartDate,
      comparableEndDate,
      compareToStartDate,
      compareToEndDate,
    });

    if (ranges) {
      const response = await sendRequest(() =>
        axios.post(`/api/consumer/sonar/calculations`, {
          loginRequest: {
            projectName,
            projectPassword,
          },
          calculationsRequest: {
            ranges: ranges,
          },
        }),
      );

      return { content: response.data.calculations, serverUrl };
    } else {
      return rejectWithValue(null);
    }
  },
);

export const getConsumerSonarNavig8 = createAsyncThunk(
  GET_CONSUMER_SONAR_NAVIG8,
  async (_, { rejectWithValue, getState, dispatch }) => {
    const {
      [SONAR_RANGE_TYPE.COMPARABLE]: { startDate: comparableStartDate, endDate: comparableEndDate },
      [SONAR_RANGE_TYPE.COMPARE_TO]: { startDate: compareToStartDate, endDate: compareToEndDate },
    } = getState().sonarDashboard.dateRanges;
    const { projectName, projectPassword } = getState().consumer.sonar;

    const ranges = constructSonarRequestDates({
      comparableStartDate,
      comparableEndDate,
      compareToStartDate,
      compareToEndDate,
    });

    const controller = new AbortController();
    dispatch(setNavig8AbortController(controller));

    if (ranges) {
      const response = await sendRequest(
        () =>
          axios.post(
            `/api/consumer/sonar/navig8`,
            {
              loginRequest: {
                projectName,
                projectPassword,
              },
              calculationsRequest: {
                ranges: ranges,
              },
            },
            { signal: controller.signal },
          ),
        {
          onError: (e) => {
            dispatch(cancelNavig8());
            return rejectWithValue(e);
          },
        },
      );

      dispatch(setNavig8AbortController(null));
      return response.data;
    } else {
      dispatch(cancelNavig8());
      return rejectWithValue(null);
    }
  },
);

export const replyConsumerSonarNavig8 = createAsyncThunk(
  REPLY_CONSUMER_SONAR_NAVIG8,
  async ({ reply }, { rejectWithValue, getState, dispatch }) => {
    const { thread } = getState().sonarDashboard.navig8;
    const { projectName, projectPassword } = getState().consumer.sonar;

    const controller = new AbortController();
    dispatch(setNavig8AbortController(controller));

    const response = await sendRequest(
      () =>
        axios.post(
          `/api/consumer/sonar/navig8/reply`,
          {
            projectRequest: {
              projectName,
              projectPassword,
            },
            request: {
              reply,
              threadId: thread,
            },
          },
          { signal: controller.signal },
        ),
      {
        onError: (e) => {
          dispatch(cancelNavig8());
          return rejectWithValue(e);
        },
      },
    );

    dispatch(setNavig8AbortController(null));
    return response.data;
  },
);
